"use client";

import React, { useEffect, useState } from "react";
import { ReactSVG } from "react-svg";

import { isSafari, isClient } from "@/utils/browser";
import { getFramedQr, getQRCodeUrl } from "@/utils/qrEditorHelper";
import { getUnlockFeaturesModal } from "@/modals/UnlockFeaturesModal";
import { ModalContext } from "@/contexts/ModalContext";
import { QREditorModalDataProps } from "@/templates/QrEditorTemplates/qr-layout/types";
import { useLogin } from "@/hooks/auth/useLogin";

import { STATIC_QR } from "@/types/qr";
import { Typography } from "../Typography";
import { useQRStyle } from "@/hooks/qr/useQR";
import { isFramedQr } from "@/utils/dashboard";
import { FrameType } from "../QRFrame/types";
import { Loading } from "../Loading";
import { QREditorContext } from "@/contexts/QREditorContext";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { IoQrCode } from "react-icons/io5";

export const QRPreviewStyle = ({
  data,
  modalData,
  showEmptyPreview,
  defaultPreviewTitle,
}: {
  data: any;
  modalData?: QREditorModalDataProps;
  showEmptyPreview?: boolean;
  defaultPreviewTitle: string;
}) => {
  const modalContext = React.useContext(ModalContext);
  const { isUserLoggedIn } = useLogin();
  const { isQrLoading } = React.useContext<QREditorContextProps<any>>(QREditorContext);

  const { ref, updateQRCode, url, setUrl, qrCode } = useQRStyle();
  const frame = data?.qrStyle;

  const [urlQr, setUrlQr] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const getFrameWrapperStyle = () => {
    return `qr-preview__canvas__wrapper ${
      frame?.frameStyle === "none" ||
      frame?.frameStyle === undefined ||
      data?.qrStyle?.styleType === "AI"
        ? "qr-preview__canvas__wrapper--none"
        : ""
    }`;
  };

  useEffect(() => {
    updateQRCode({
      qrUrl: STATIC_QR.includes(data.type) ? getQRCodeUrl(data, true) : url,
      qrStyle: data?.qrStyle,
    });
  }, [data, url, updateQRCode]);

  useEffect(() => {
    if (isClient() && isSafari() && qrCode) {
      setTimeout(() => {
        qrCode?.getRawData().then((data) => data && setUrlQr(URL.createObjectURL(data)));
      }, 100);
    }
  }, [data?.qrStyle, qrCode]);

  useEffect(() => {
    if (data.qrCodeReference) {
      const qrUrl = getQRCodeUrl(data, true);
      if (qrUrl) setUrl(qrUrl);
    }
  }, [data.qrCodeReference]);

  const handleContextMenu = (event) => {
    if (isUserLoggedIn()) return;

    event.preventDefault();
    if (!modalData) return;

    modalContext.handleModal({
      content: getUnlockFeaturesModal({
        onConfirmButton: () => {
          modalContext.handleModal();
        },
        data: modalData,
      }),
      modalClassName: "unlock-features-modal",
    });
  };

  const getFramedQrCodeComponent = () => {
    return isLoading ? (
      <Loading />
    ) : (
      getFramedQr(data?.qrStyle?.frameStyle as FrameType, ref, data, urlQr)
    );
  };

  const getNotFramedQrCodeComponent = () => {
    const qrStyleType = data?.qrStyle?.styleType;
    const selectedQrCode = data?.ai?.selectedImageUrl;
    return qrStyleType === "AI" ? (
      selectedQrCode ? (
        <img src={selectedQrCode} />
      ) : (
        <img src="/images/empty-logo.png" />
      )
    ) : (
      <div ref={ref}></div>
    );
  };

  if (!data?.type && showEmptyPreview) {
    return (
      <div className="qr-preview__layout__image">
        <div className="qr-preview__canvas">
          <div className="frame">
            <div id="empty-preview">
              <ReactSVG src={`/svg/icon-choose.svg`} />
              <Typography
                Tag="p"
                classname="u-spacing-5"
              >
                {defaultPreviewTitle}
              </Typography>
            </div>
          </div>
        </div>
        <ReactSVG
          src={`/images/phone-previews/phone-empty.svg`}
          className="preview-frame"
        />
      </div>
    );
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="qr-preview__layout__image">
      <div
        className="qr-preview__canvas"
        id="canvas"
      >
        {isQrLoading ? (
          <div className="loader__container">
            <Loading
              dataQA="loader"
              className="loader preview"
              icon={<IoQrCode />}
            />
          </div>
        ) : (
          <div className="frame">
            <div
              className={getFrameWrapperStyle()}
              onContextMenu={handleContextMenu}
            >
              {!isFramedQr(false, data?.qrStyle)
                ? getNotFramedQrCodeComponent()
                : getFramedQrCodeComponent()}
            </div>
          </div>
        )}
      </div>

      <ReactSVG
        src={`/images/phone-previews/phone-empty.svg`}
        className="preview-frame"
      />
    </div>
  );
};
